import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { DashboardHeader, StatCard, Tabs, StatusBadge, Empty, fmtTs, fmtTime12 } from "../components/DashboardUI";
import ChangePasswordModal from "../components/ChangePasswordModal";
import OwnerAssistantPanel from "../components/OwnerAssistantPanel";

export default function OwnerDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("testimonials");
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [newStaffOpen, setNewStaffOpen] = useState(false);
  const [newServiceOpen, setNewServiceOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [t, s, c, st, b, sx] = await Promise.all([
        api.get("/testimonials?all=true"),
        api.get("/services?all=true"),
        api.get("/staff/customers"),
        api.get("/owner/staff"),
        api.get("/bookings"),
        api.get("/owner/stats"),
      ]);
      setTestimonials(t.data); setServices(s.data); setCustomers(c.data);
      setStaff(st.data); setBookings(b.data); setStats(sx.data);
    } catch { nav("/admin-owner/login"); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/admin-owner/login");
    if (user.role !== "owner" && user.role !== "admin") return nav("/");
    load();
  }, [user, loading]);

  const ownerReview = async (kind, id, decision, edited_message, note) => {
    try {
      const body = { decision, note: note || "" };
      if (edited_message) body.edited_message = edited_message;
      await api.patch(`/${kind}/${id}/owner-review`, body);
      toast.success(`Sent to admin (${decision})`);
      setEditTestimonial(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm("Remove this staff?")) return;
    try { await api.delete(`/owner/staff/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const editCustomerSave = async (data) => {
    try { await api.patch(`/owner/customers/${editCustomer.id}`, data); toast.success("Customer updated"); setEditCustomer(null); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="owner-dashboard">
      <DashboardHeader title="Owner Console" subtitle={`${user.name} · Site Owner`} user={user} onLogout={logout} onChangePassword={() => setPwdOpen(true)} />
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Customers" value={stats.customers} />
          <StatCard label="Pending Bookings" value={stats.bookings_pending} accent />
          <StatCard label="Testimonials to Review" value={stats.testimonials_to_review} accent />
          <StatCard label="Staff" value={stats.staff} />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <Tabs current={tab} onChange={setTab} prefix="owner-tab" tabs={[
          { k: "testimonials", label: "Testimonials", count: stats?.testimonials_to_review },
          { k: "services", label: "Services" },
          { k: "promotions", label: "Promotions" },
          { k: "customers", label: "Customers" },
          { k: "feedback", label: "Feedback" },
          { k: "staff", label: "Staff" },
          { k: "bookings", label: "Bookings" },
          { k: "ai", label: "AI Assistant" },
          { k: "changes", label: "Change Requests" },
        ]} />
        <p className="text-xs text-[var(--muted)] italic mt-3">Owner pre-approves testimonials & services; admin (DarkWebx03) gives final approval before going public.</p>

        <div className="mt-6 pb-16">
          {tab === "testimonials" && (
            <TestimonialsReview items={testimonials} onEdit={(t) => setEditTestimonial(t)} onReview={(id, dec) => ownerReview("testimonials", id, dec)} />
          )}
          {tab === "services" && (
            <ServicesPanel items={services}
              onReview={(id, dec) => ownerReview("services", id, dec)}
              onAdd={() => setNewServiceOpen(true)} />
          )}
          {tab === "promotions" && <PromotionsPanel />}
          {tab === "customers" && (
            <CustomersFull items={customers} onEdit={(c) => setEditCustomer(c)} />
          )}
          {tab === "feedback" && <FeedbackPanel />}
          {tab === "staff" && (
            <StaffPanel items={staff} onAdd={() => setNewStaffOpen(true)} onDelete={deleteStaff} onPhotoUpdated={load} />
          )}
          {tab === "bookings" && <BookingsView items={bookings} onRefresh={load} />}
          {tab === "ai" && <OwnerAssistantPanel />}
          {tab === "changes" && <ChangeRequestsTab />}
        </div>
      </div>

      {editTestimonial && (
        <EditTestimonial t={editTestimonial} onClose={() => setEditTestimonial(null)} onSave={(msg) => ownerReview("testimonials", editTestimonial.id, "approved", msg)} />
      )}
      {editCustomer && (
        <EditCustomerModal c={editCustomer} onClose={() => setEditCustomer(null)} onSave={editCustomerSave} />
      )}
      {newStaffOpen && <NewStaffModal onClose={() => setNewStaffOpen(false)} onCreated={() => { setNewStaffOpen(false); load(); }} />}
      {newServiceOpen && <NewServiceModal onClose={() => setNewServiceOpen(false)} onCreated={() => { setNewServiceOpen(false); load(); }} />}
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}

/* ---- Testimonials Review ---- */
function TestimonialsReview({ items, onEdit, onReview }) {
  if (!items.length) return <Empty msg="No testimonials." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(t => (
        <div key={t.id} className="bg-white border border-[var(--border)] p-6" data-testid={`owner-testimonial-${t.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2">
              <StatusBadge s={t.owner_reviewed ? (t.owner_decision || "pending") : "pending"} />
              <span className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Final: {t.status}</span>
            </div>
            <span className="text-xs text-[var(--muted)]">{fmtTs(t.created_at)}</span>
          </div>
          <p className="font-display text-lg italic mb-3">"{t.edited_message || t.message}"</p>
          {t.edited_message && <p className="text-[10px] text-[var(--accent)] mb-3">Edited from: "{t.message}"</p>}
          <div className="flex items-center justify-between">
            <div><p className="font-medium">{t.name}</p><p className="text-xs text-[var(--muted)] uppercase tracking-widest">{t.role}</p></div>
            {!t.owner_reviewed || t.owner_decision === "pending" ? (
              <div className="flex gap-2">
                <button onClick={() => onEdit(t)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded" data-testid={`owner-edit-${t.id}`}>Edit & Approve</button>
                <button onClick={() => onReview(t.id, "approved")} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded" data-testid={`owner-approve-${t.id}`}>Approve</button>
                <button onClick={() => onReview(t.id, "declined")} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded" data-testid={`owner-decline-${t.id}`}>Decline</button>
              </div>
            ) : (
              <p className="text-xs text-[var(--accent)] uppercase tracking-widest">Sent to admin</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditTestimonial({ t, onClose, onSave }) {
  const [msg, setMsg] = useState(t.edited_message || t.message);
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="edit-testimonial-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">Edit Testimonial</h3>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        <div className="p-7 space-y-4">
          <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={6} data-testid="edit-testimonial-text"
            className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 text-sm" />
          <p className="text-xs text-[var(--muted)] italic">After approving, this will be sent to admin (DarkWebx03) for final approval before publishing.</p>
          <button onClick={() => onSave(msg)} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="edit-testimonial-save">Save & Approve</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Services ---- */
function ServicesPanel({ items, onReview, onAdd }) {
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="add-service-btn">+ Add Service</button>
      </div>
      {!items.length ? <Empty msg="No services." /> : (
        <div className="bg-white border border-[var(--border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
              <tr><th className="text-left p-4">Service</th><th className="text-left p-4">Price</th><th className="text-left p-4">Owner</th><th className="text-left p-4">Public</th><th className="text-right p-4">Action</th></tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-t border-[var(--border)]" data-testid={`owner-service-${s.id}`}>
                  <td className="p-4"><p className="font-medium">{s.name}</p><p className="text-xs text-[var(--muted)] mt-1">{s.description}</p></td>
                  <td className="p-4">AED {s.price_aed}<br/><span className="text-xs text-[var(--muted)]">{s.duration_min} min</span></td>
                  <td className="p-4"><StatusBadge s={s.owner_reviewed ? (s.owner_decision || "pending") : "pending"} /></td>
                  <td className="p-4"><StatusBadge s={s.status} /></td>
                  <td className="p-4 text-right">
                    {!s.owner_reviewed && (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => onReview(s.id, "approved")} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded" data-testid={`owner-svc-approve-${s.id}`}>Approve</button>
                        <button onClick={() => onReview(s.id, "declined")} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded" data-testid={`owner-svc-decline-${s.id}`}>Decline</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function NewServiceModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", description: "", price_aed: 100, duration_min: 60, image_url: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/services", f); toast.success("Service submitted — pending admin approval"); onCreated(); }
    catch { toast.error("Failed"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="new-service-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">New Service</h3>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        <form onSubmit={submit} className="p-7 space-y-3">
          <input required placeholder="Name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="svc-name" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea required placeholder="Description" value={f.description} onChange={e=>setF({...f, description: e.target.value})} data-testid="svc-desc" rows={3} className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="number" placeholder="Price AED" value={f.price_aed} onChange={e=>setF({...f, price_aed: +e.target.value})} data-testid="svc-price" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
            <input required type="number" placeholder="Duration min" value={f.duration_min} onChange={e=>setF({...f, duration_min: +e.target.value})} data-testid="svc-dur" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          </div>
          <input placeholder="Image URL (optional)" value={f.image_url} onChange={e=>setF({...f, image_url: e.target.value})} data-testid="svc-img" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="svc-submit">{loading ? "..." : "Submit (admin approval needed)"}</button>
        </form>
      </div>
    </div>
  );
}

/* ---- Customers (full edit) ---- */
function CustomersFull({ items, onEdit }) {
  if (!items.length) return <Empty msg="No customers." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Phone</th><th className="text-left p-4">Visits</th><th className="text-left p-4">Last Service</th><th className="text-right p-4">Action</th></tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`owner-customer-${c.id}`}>
              <td className="p-4 font-medium">{c.name}</td>
              <td className="p-4 text-xs">{c.email}</td>
              <td className="p-4">{c.phone}</td>
              <td className="p-4">{c.bookings_count}</td>
              <td className="p-4 text-xs">{c.last_service ? `${c.last_service} · ${c.last_visit}` : "—"}</td>
              <td className="p-4 text-right">
                <button onClick={() => onEdit(c)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`owner-edit-cust-${c.id}`}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditCustomerModal({ c, onClose, onSave }) {
  const [f, setF] = useState({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="edit-customer-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">Edit Customer</h3>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(f); }} className="p-7 space-y-3">
          <input value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="cust-name" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input type="email" value={f.email} onChange={e=>setF({...f, email: e.target.value})} data-testid="cust-email" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} data-testid="cust-phone" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <button type="submit" className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="cust-save">Save Changes</button>
        </form>
      </div>
    </div>
  );
}

/* ---- Staff ---- */
function StaffPanel({ items, onAdd, onDelete, onPhotoUpdated }) {
  const [resetResult, setResetResult] = useState(null);

  const resetPassword = async (s) => {
    if (!window.confirm(`Issue a new temporary password for ${s.name}? Their existing password will stop working immediately.`)) return;
    try {
      const r = await api.post(`/owner/staff/${s.id}/reset-password`);
      setResetResult({ name: s.name, ...r.data });
      toast.success("New temporary password issued");
    } catch {
      toast.error("Could not reset password");
    }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="add-staff-btn">+ Generate Staff Access</button>
      </div>
      {!items.length ? <Empty msg="No staff yet. Click 'Generate Staff Access' to add reception or agents." /> : (
        <div className="bg-white border border-[var(--border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
              <tr><th className="text-left p-4">Photo</th><th className="text-left p-4">Name</th><th className="text-left p-4">Username / Email</th><th className="text-left p-4">Role</th><th className="text-left p-4">Created</th><th className="text-right p-4">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-t border-[var(--border)]" data-testid={`owner-staff-${s.id}`}>
                  <td className="p-3">
                    <StaffPhotoCell staff={s} onUpdated={onPhotoUpdated} />
                  </td>
                  <td className="p-4 font-medium">{s.name}</td>
                  <td className="p-4 text-xs">{s.username || s.email || "—"}</td>
                  <td className="p-4 text-xs uppercase tracking-widest">{s.role}</td>
                  <td className="p-4 text-xs">{fmtTs(s.created_at)}</td>
                  <td className="p-4 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => resetPassword(s)} className="text-xs px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white rounded transition-colors" data-testid={`reset-pwd-${s.id}`}>Reset Password</button>
                    <button onClick={() => onDelete(s.id)} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 rounded" data-testid={`del-staff-${s.id}`}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {resetResult && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="reset-pwd-result">
          <div className="bg-white max-w-md w-full border border-[var(--border)]">
            <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
              <h3 className="font-display text-2xl">Password Reset</h3>
              <button onClick={() => setResetResult(null)} className="text-2xl">×</button>
            </div>
            <div className="p-7">
              <p className="text-sm mb-3">Share this with <strong>{resetResult.name}</strong>. They will be required to change it on next login.</p>
              <div className="bg-[var(--bg)] border border-[var(--border)] p-4 space-y-2 mb-4">
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest">Username</p>
                <p className="font-medium">{resetResult.username}</p>
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest mt-3">New Temporary Password</p>
                <p className="font-mono text-lg text-[var(--accent)]">{resetResult.temp_password}</p>
              </div>
              <button onClick={() => setResetResult(null)} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="reset-pwd-done">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StaffPhotoCell({ staff, onUpdated }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2MB"); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.patch(`/owner/staff/${staff.id}/photo`, { photo_url: dataUrl });
      toast.success("Photo updated");
      onUpdated?.();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      {staff.photo_url ? (
        <img src={staff.photo_url} alt={staff.name} className="w-10 h-10 rounded-full object-cover border border-[var(--border)]" data-testid={`staff-photo-${staff.id}`} />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[var(--bg)] border border-[var(--border)] flex items-center justify-center text-[var(--muted)] text-xs">
          <i className="fa-regular fa-user"></i>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`staff-photo-input-${staff.id}`} />
      <button onClick={() => inputRef.current?.click()} disabled={busy} className="text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline disabled:opacity-50" data-testid={`upload-photo-${staff.id}`}>
        {busy ? "..." : (staff.photo_url ? "Change" : "Upload")}
      </button>
    </div>
  );
}

function NewStaffModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", username: "", role: "agent" });
  const [loading, setLoading] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  const [createdUsername, setCreatedUsername] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { name: f.name, role: f.role };
      if (f.email) payload.email = f.email;
      if (f.username) payload.username = f.username;
      const r = await api.post("/owner/staff", payload);
      setTempPwd(r.data.temp_password);
      setCreatedUsername(r.data.username);
      toast.success("Staff created");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="new-staff-modal">
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">{tempPwd ? "Staff Created" : "Create Staff Account"}</h3>
          <button onClick={() => { onClose(); if (tempPwd) onCreated(); }} className="text-2xl">×</button>
        </div>
        <div className="p-7">
          {tempPwd ? (
            <div data-testid="temp-pwd-result">
              <p className="text-sm mb-3">Account created. Share these credentials with the staff member — they should change the password on first login.</p>
              <div className="bg-[var(--bg)] border border-[var(--border)] p-4 space-y-2 mb-4">
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest">Username</p>
                <p className="font-medium">{createdUsername}</p>
                {f.email && (<>
                  <p className="text-xs text-[var(--muted)] uppercase tracking-widest mt-3">Email</p>
                  <p className="font-medium">{f.email}</p>
                </>)}
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest mt-3">Temporary Password</p>
                <p className="font-mono text-lg text-[var(--accent)]">{tempPwd}</p>
              </div>
              <button onClick={() => onCreated()} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="temp-pwd-done">Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input required placeholder="Full name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="new-staff-name" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
              <input placeholder="Username (optional — auto-generated if blank)" value={f.username} onChange={e=>setF({...f, username: e.target.value})} data-testid="new-staff-username" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
              <input type="email" placeholder="Email (optional)" value={f.email} onChange={e=>setF({...f, email: e.target.value})} data-testid="new-staff-email" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
              <select value={f.role} onChange={e=>setF({...f, role: e.target.value})} data-testid="new-staff-role" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5">
                <option value="agent">Salon Staff (Agent — read-only)</option>
                <option value="receptionist">Reception (manage bookings & assign staff)</option>
              </select>
              <p className="text-[11px] text-[var(--muted)]">Email is optional. Username + password is enough to log in. If username is blank, we'll auto-generate one for you.</p>
              <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="new-staff-submit">
                {loading ? "Creating..." : "Generate Access"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- Bookings view (owner) ---- */
function BookingsView({ items, onRefresh }) {
  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      if (r.data?.whatsapp_link) window.open(r.data.whatsapp_link, "_blank", "noopener");
      onRefresh?.();
    } catch { toast.error("Failed"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Permanently delete this booking? Use this for test entries.")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Booking deleted"); onRefresh?.(); }
    catch { toast.error("Could not delete"); }
  };

  if (!items.length) return <Empty msg="No bookings." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Service</th><th className="text-left p-4">Date</th><th className="text-left p-4">Phone</th><th className="text-left p-4">Status</th><th className="text-left p-4">Assigned</th><th className="text-left p-4">Created</th><th className="text-right p-4">Actions</th></tr>
        </thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} className="border-t border-[var(--border)]" data-testid={`owner-booking-${b.id}`}>
              <td className="p-4">{b.name}</td>
              <td className="p-4">{b.service}</td>
              <td className="p-4">{b.date} {fmtTime12(b.time)}</td>
              <td className="p-4 text-xs">
                <a href={`https://wa.me/${(b.phone||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline">
                  <i className="fa-brands fa-whatsapp mr-1"></i>{b.phone}
                </a>
              </td>
              <td className="p-4">
                <StatusBadge s={b.status} />
                {b.cancellation && <p className="text-[10px] text-[var(--muted)] italic mt-1">"{b.cancellation.reason || '—'}" {b.cancellation.receptionist_note && `· note: ${b.cancellation.receptionist_note}`}</p>}
              </td>
              <td className="p-4 text-xs">{b.assigned_staff_name || "—"}</td>
              <td className="p-4 text-xs">{fmtTs(b.created_at)}</td>
              <td className="p-4">
                <div className="flex gap-2 justify-end flex-wrap">
                  {b.status === "pending" && (
                    <>
                      <button onClick={() => updateStatus(b.id, "approved")} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700" data-testid={`owner-approve-booking-${b.id}`}>Confirm</button>
                      <button onClick={() => updateStatus(b.id, "cancelled")} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700" data-testid={`owner-decline-booking-${b.id}`}>Decline</button>
                    </>
                  )}
                  <button onClick={() => deleteBooking(b.id)} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 rounded" data-testid={`owner-delete-booking-${b.id}`}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- Change Requests Tab (owner submits major changes for admin approval) ---- */
function ChangeRequestsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", impact: "", estimated_downtime: "None",
    affected_collections: "", proposed_changes: "",
  });
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try { const r = await api.get("/change-requests"); setItems(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        impact: form.impact,
        estimated_downtime: form.estimated_downtime,
        affected_collections: form.affected_collections.split(",").map(s => s.trim()).filter(Boolean),
        proposed_changes: form.proposed_changes ? safeParseJson(form.proposed_changes) : {},
      };
      const r = await api.post("/change-requests", payload);
      setLastResult(r.data);
      toast.success("Sent to admin (DarkWebx03) for approval");
      setForm({ title: "", description: "", impact: "", estimated_downtime: "None", affected_collections: "", proposed_changes: "" });
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-[var(--muted)] italic max-w-2xl">For any DB-affecting change (price changes, bulk deletes, schema updates), submit a request here. Admin will receive an approval link by email and a WhatsApp share link for instant decision.</p>
        <button onClick={() => setShowForm(!showForm)} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="cr-toggle-form">
          {showForm ? "Hide form" : "+ New Change Request"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-[var(--border)] p-6 mb-6 space-y-3" data-testid="cr-form">
          <input required minLength={4} placeholder="Title (e.g. Update all facial prices)" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} data-testid="cr-title" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea required minLength={10} rows={3} placeholder="Description — what & why" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} data-testid="cr-desc" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea required minLength={4} rows={2} placeholder="Impact — what changes for customers/staff" value={form.impact} onChange={e=>setForm({...form, impact: e.target.value})} data-testid="cr-impact" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input placeholder="Estimated downtime (e.g. None, 5 min)" value={form.estimated_downtime} onChange={e=>setForm({...form, estimated_downtime: e.target.value})} data-testid="cr-downtime" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input placeholder="Affected collections (comma separated, e.g. services,bookings)" value={form.affected_collections} onChange={e=>setForm({...form, affected_collections: e.target.value})} data-testid="cr-collections" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea rows={3} placeholder='Proposed changes JSON (optional, e.g. {"prices":{"facial": 500}})' value={form.proposed_changes} onChange={e=>setForm({...form, proposed_changes: e.target.value})} data-testid="cr-changes" className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 font-mono text-xs" />
          <button type="submit" disabled={loading} className="btn-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest" data-testid="cr-submit">
            {loading ? "Submitting..." : "Submit for Admin Approval"}
          </button>
        </form>
      )}

      {lastResult && (
        <div className="bg-white border border-[var(--border)] p-5 mb-6" data-testid="cr-last-result">
          <p className="text-xs uppercase tracking-widest text-[var(--accent)]">Just submitted</p>
          <p className="font-display text-xl mt-1">{lastResult.title}</p>
          <p className="text-xs text-[var(--muted)] mt-2">Approval link: <a href={lastResult.approval_url} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">{lastResult.approval_url}</a></p>
          <p className="text-xs text-[var(--muted)] mt-1">WhatsApp to admin: <a href={lastResult.whatsapp_link} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">share via WhatsApp</a></p>
        </div>
      )}

      {!items.length ? <Empty msg="No change requests yet." /> : (
        <div className="bg-white border border-[var(--border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
              <tr><th className="text-left p-4">Title</th><th className="text-left p-4">Submitted</th><th className="text-left p-4">Downtime</th><th className="text-left p-4">Status</th><th className="text-right p-4">Approval Link</th></tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`cr-row-${c.id}`}>
                  <td className="p-4"><p className="font-medium">{c.title}</p><p className="text-xs text-[var(--muted)] mt-1">{c.description.slice(0, 80)}{c.description.length > 80 ? "…" : ""}</p></td>
                  <td className="p-4 text-xs">{c.submitted_by_name}<br/>{fmtTs(c.created_at)}</td>
                  <td className="p-4 text-xs">{c.estimated_downtime}</td>
                  <td className="p-4"><StatusBadge s={c.status} />{c.admin_note && <p className="text-[10px] italic mt-1">"{c.admin_note}"</p>}</td>
                  <td className="p-4 text-right text-xs"><a href={`/admin/approve/${c.approval_token}`} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">Open</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function safeParseJson(s) {
  try { return JSON.parse(s); } catch { return { _raw: s }; }
}

/* ---- Promotions ---- */
function PromotionsPanel() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", discount_percent: 0, discount_aed: 0, valid_until: "", active: true, image_url: "" });

  const resetForm = () => setForm({ title: "", description: "", discount_percent: 0, discount_aed: 0, valid_until: "", active: true, image_url: "" });
  const load = async () => { try { const r = await api.get("/promotions?active_only=false"); setItems(r.data); } catch {} };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/promotions/${editing}`, form);
        toast.success("Promotion updated — re-submitted for admin approval");
      } else {
        await api.post("/promotions", form);
        toast.success("Promotion submitted for admin approval");
      }
      setAdding(false); setEditing(null); resetForm(); load();
    } catch { toast.error("Failed"); }
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "", description: p.description || "",
      discount_percent: p.discount_percent || 0, discount_aed: p.discount_aed || 0,
      valid_until: p.valid_until || "", active: p.active !== false, image_url: p.image_url || "",
    });
    setAdding(true);
  };
  const toggleActive = async (p) => { try { await api.patch(`/promotions/${p.id}`, { ...p, active: !p.active }); load(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete promo?")) return; try { await api.delete(`/promotions/${id}`); load(); } catch { toast.error("Failed"); } };

  const statusBadge = (s) => {
    const map = {
      approved: "bg-green-100 text-green-800",
      pending: "bg-amber-100 text-amber-800",
      rejected: "bg-red-100 text-red-800",
    };
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${map[s] || "bg-gray-100"}`}>{s || "—"}</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-[var(--muted)] italic max-w-2xl">Create discounts &amp; offers. Owner submissions are sent to <b>Admin (DarkWebx03)</b> for approval before appearing on the public landing page. Edits to approved promos re-trigger approval.</p>
        <button onClick={() => { if (adding) { setEditing(null); resetForm(); } setAdding(!adding); }} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="promo-toggle">
          {adding ? "Cancel" : "+ New Promotion"}
        </button>
      </div>
      {adding && (
        <form onSubmit={submit} className="bg-white border border-[var(--border)] p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="promo-form">
          <input required placeholder="Title (e.g. 20% OFF Bridal Hair)" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} data-testid="promo-title" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input placeholder="Valid until (YYYY-MM-DD)" type="date" value={form.valid_until} onChange={e=>setForm({...form, valid_until: e.target.value})} data-testid="promo-valid" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <textarea rows={2} placeholder="Description" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} data-testid="promo-desc" className="md:col-span-2 bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input type="number" placeholder="Discount %" value={form.discount_percent} onChange={e=>setForm({...form, discount_percent: +e.target.value})} data-testid="promo-pct" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input type="number" placeholder="Discount AED" value={form.discount_aed} onChange={e=>setForm({...form, discount_aed: +e.target.value})} data-testid="promo-aed" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input placeholder="Image URL (optional)" value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})} data-testid="promo-img" className="md:col-span-2 bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <button type="submit" className="md:col-span-2 btn-gold rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="promo-submit">{editing ? "Update — Send for Approval" : "Submit for Admin Approval"}</button>
        </form>
      )}
      {!items.length ? <Empty msg="No promotions yet." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(p => (
            <div key={p.id} className="bg-white border border-[var(--border)] p-5" data-testid={`promo-row-${p.id}`}>
              <div className="flex justify-between items-start mb-2 gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-display text-xl">{p.title}</p>
                    {statusBadge(p.status)}
                  </div>
                  <p className="text-sm text-[var(--muted)]">{p.description}</p>
                  {p.status === "rejected" && p.rejection_reason && (
                    <p className="text-xs text-red-600 italic mt-1">Rejected: "{p.rejection_reason}"</p>
                  )}
                  {p.status === "pending" && (
                    <p className="text-xs text-amber-700 italic mt-1">Awaiting admin approval — not yet visible on the landing page.</p>
                  )}
                </div>
                <div className="text-right">
                  {p.discount_percent > 0 && <p className="font-display text-3xl text-[var(--accent)]">{p.discount_percent}%</p>}
                  {p.discount_aed > 0 && <p className="font-display text-2xl text-[var(--accent)]">-{p.discount_aed}</p>}
                </div>
              </div>
              {p.valid_until && <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Until {p.valid_until}</p>}
              <div className="flex justify-end gap-2 mt-3 flex-wrap">
                <button onClick={() => startEdit(p)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`promo-edit-${p.id}`}>Edit</button>
                <button onClick={() => toggleActive(p)} className={`text-xs px-3 py-1.5 rounded ${p.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`} data-testid={`promo-toggle-${p.id}`}>
                  {p.active ? "Active" : "Paused"}
                </button>
                <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 rounded" data-testid={`promo-del-${p.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Feedback ---- */
function FeedbackPanel() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const r = await api.get("/owner/feedback"); setItems(r.data); } catch {} };
  useEffect(() => { load(); }, []);
  if (!items.length) return <Empty msg="No customer feedback yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(f => (
        <div key={f.id} className="bg-white border border-[var(--border)] p-6" data-testid={`feedback-${f.id}`}>
          <div className="flex justify-between items-start mb-2 gap-3">
            <div>
              <p className="font-display text-xl">{f.subject}</p>
              <p className="text-xs text-[var(--muted)]">{f.customer_name} · {fmtTs(f.created_at)}</p>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[var(--accent)]">{f.contact_via}</span>
          </div>
          <p className="text-sm leading-relaxed mb-3">{f.message}</p>
          <div className="flex gap-2 text-xs flex-wrap">
            <a href={`mailto:${f.customer_email}?subject=Re: ${f.subject}`} className="px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`fb-email-${f.id}`}>
              <i className="fa-regular fa-envelope mr-1"></i> Email
            </a>
            {f.customer_phone && (
              <a href={`https://wa.me/${(f.customer_phone||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`fb-wa-${f.id}`}>
                <i className="fa-brands fa-whatsapp mr-1"></i> WhatsApp
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
